import type {
  WhiteboardElement,
  PathElement,
  RectElement,
  EllipseElement,
  LineElement,
  TextElement,
  StickyElement,
} from '../types/whiteboard.types.ts';

export function drawElement(ctx: CanvasRenderingContext2D, element: WhiteboardElement) {
  ctx.save();
  ctx.strokeStyle = element.color;
  ctx.fillStyle = element.color;
  ctx.lineWidth = element.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (element.type) {
    case 'path':
      drawPath(ctx, element);
      break;
    case 'rectangle':
      drawRect(ctx, element);
      break;
    case 'ellipse':
      drawEllipse(ctx, element);
      break;
    case 'line':
    case 'arrow':
      drawLine(ctx, element, element.type === 'arrow');
      break;
    case 'text':
      drawText(ctx, element);
      break;
    case 'sticky':
      drawSticky(ctx, element);
      break;
  }

  ctx.restore();
}

function drawPath(ctx: CanvasRenderingContext2D, element: PathElement) {
  if (element.points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(element.points[0].x, element.points[0].y);
  for (let i = 1; i < element.points.length; i++) {
    ctx.lineTo(element.points[i].x, element.points[i].y);
  }
  ctx.stroke();
}

function drawRect(ctx: CanvasRenderingContext2D, element: RectElement) {
  if (element.fill) {
    ctx.fillStyle = element.fill;
    ctx.fillRect(element.x, element.y, element.width, element.height);
  }
  ctx.strokeRect(element.x, element.y, element.width, element.height);
}

function drawEllipse(ctx: CanvasRenderingContext2D, element: EllipseElement) {
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const rx = Math.abs(element.width) / 2;
  const ry = Math.abs(element.height) / 2;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  if (element.fill) {
    ctx.fillStyle = element.fill;
    ctx.fill();
  }
  ctx.stroke();
}

function drawLine(ctx: CanvasRenderingContext2D, element: LineElement, withArrow: boolean) {
  ctx.beginPath();
  ctx.moveTo(element.x1, element.y1);
  ctx.lineTo(element.x2, element.y2);
  ctx.stroke();

  if (!withArrow) return;

  const angle = Math.atan2(element.y2 - element.y1, element.x2 - element.x1);
  const head = Math.max(10, element.strokeWidth * 3);
  ctx.beginPath();
  ctx.moveTo(element.x2, element.y2);
  ctx.lineTo(
    element.x2 - head * Math.cos(angle - Math.PI / 6),
    element.y2 - head * Math.sin(angle - Math.PI / 6)
  );
  ctx.moveTo(element.x2, element.y2);
  ctx.lineTo(
    element.x2 - head * Math.cos(angle + Math.PI / 6),
    element.y2 - head * Math.sin(angle + Math.PI / 6)
  );
  ctx.stroke();
}

function drawText(ctx: CanvasRenderingContext2D, element: TextElement) {
  ctx.fillStyle = element.color;
  ctx.font = `${element.fontSize}px system-ui, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillText(element.text, element.x, element.y);
}

function drawSticky(ctx: CanvasRenderingContext2D, element: StickyElement) {
  ctx.fillStyle = element.fill;
  ctx.fillRect(element.x, element.y, element.width, element.height);
  ctx.strokeStyle = 'rgba(0,0,0,0.15)';
  ctx.strokeRect(element.x, element.y, element.width, element.height);

  ctx.fillStyle = '#1f2937';
  ctx.font = '14px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  const lines = wrapText(ctx, element.text, element.width - 16);
  lines.forEach((line, i) => {
    ctx.fillText(line, element.x + 8, element.y + 8 + i * 18);
  });
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

export function renderWhiteboard(
  canvas: HTMLCanvasElement,
  elements: WhiteboardElement[],
  backgroundColor: string
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const gridColor = 'rgba(0,0,0,0.04)';
  const step = 32;
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  for (let x = 0; x < canvas.width; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvas.height);
    ctx.stroke();
  }
  for (let y = 0; y < canvas.height; y += step) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(canvas.width, y);
    ctx.stroke();
  }

  for (const element of elements) {
    drawElement(ctx, element);
  }
}

export function createThumbnail(
  elements: WhiteboardElement[],
  backgroundColor: string,
  width = 320,
  height = 180
): string {
  const full = document.createElement('canvas');
  full.width = 1600;
  full.height = 900;
  renderWhiteboard(full, elements, backgroundColor);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(full, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}
